import React, { useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import { Col, Row, Modal, Button, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DatePicker from "@services/ui-components/src/DatePicker/DatePicker";
import ToastComponent from "../../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../../hooks/useToast";
import {
    useDeleteDismissEmployeeMutation,
    useDismissEmployeeMutation,
    useGetBookingsForEmployeeLazyQuery,
    useGetDismissalReasonQuery,
    useUpdateDismissEmployeeMutation,
} from "@me-team/host/main/employeeList/graphql/employeeList.hooks";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { deleteEmployeeModalValidationSchema, getNextDay } from "../../utils/dateUtils";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { CustomModal, ReactSelect, SpinnerLoader, TextareaInputWithCounter } from "@services/ui-components";
import { useNavigate, useParams } from "react-router-dom";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { EmployeeDismissalReasonProxy, EmployeeProxy } from "@me-team/host/main/graphql/types";
import { Option } from "../../../Products/types";

interface FormData {
    employeeId: number;
    endOfCooperation: string;
    reasonId: number;
    comment: string;
}

const DeleteEmployeeModal: React.FC<{
    show: boolean;
    onHide: () => void;
    selectedEmployee: EmployeeProxy;
    refetch: () => void;
}> = ({ show, onHide, selectedEmployee, refetch }) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedDate =
        selectedEmployee?.dismissalEmployee?.endDateCooperation &&
        new Date(selectedEmployee?.dismissalEmployee?.endDateCooperation);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showBookingConflictModal, setShowBookingConflictModal] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormData>(null);
    const [startDate, setStartDate] = useState(null);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const handleCloseBookingConflictModal = () => setShowBookingConflictModal(false);

    const [deleteEmployee] = useDismissEmployeeMutation();
    const [updateDeleteEmployee] = useUpdateDismissEmployeeMutation();
    const [dismissDeleteEmployee] = useDeleteDismissEmployeeMutation({
        context: {
            errorType: "local",
        },
    });
    const { data: dismissalReasonData } = useGetDismissalReasonQuery();

    const dismissalReasonOptions =
        dismissalReasonData?.dismissalReason?.map((reason: EmployeeDismissalReasonProxy) => {
            return {
                value: reason?.id,
                label: reason?.name,
            };
        }) || [];

    const [getBookingsForEmployee, { data: bookingsForEmployeeData, loading: bookingsForEmployeeLoading }] =
        useGetBookingsForEmployeeLazyQuery({
            context: { errorType: "local" },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });

    const toggleConfirmDeleting = async () => {
        setShowConfirmationModal(!showConfirmationModal);
    };

    const dismissDeleting = async (event: React.MouseEvent) => {
        event.preventDefault();

        try {
            await dismissDeleteEmployee({
                variables: {
                    id: selectedEmployee?.dismissalEmployee?.id,
                    companyId: +currentCompanyId,
                    branchId: +branchId,
                },
            });
            refetch();
            onHide();
            toggleShowToast(t("Restored"));
        } catch (error) {
            console.error("Error dismissDeleting employee:", error);
        }
    };

    const confirmDeleting = async () => {
        if (!formData) {
            console.error("Form data is not defined");
            return;
        }
        try {
            setIsLoading(true);
            if (selectedEmployee?.dismissalEmployee?.id) {
                await updateDeleteEmployee({
                    variables: {
                        dismissalId: selectedEmployee?.dismissalEmployee?.id,
                        companyId: +currentCompanyId,
                        branchId: +branchId,
                        input: {
                            endOfCooperation: formData.endOfCooperation,
                            reason: formData.reasonId,
                            comment: formData.reasonId === 2 ? null : formData.comment,
                        },
                    },
                });
            } else {
                await deleteEmployee({
                    variables: {
                        employeeId: formData.employeeId,
                        companyId: +currentCompanyId,
                        branchId: +branchId,
                        input: {
                            endOfCooperation: formData.endOfCooperation,
                            reason: formData.reasonId,
                            comment: formData.reasonId === 2 ? null : formData.comment,
                        },
                    },
                });
            }
            refetch();
            toggleConfirmDeleting();
            toggleShowToast(t("Employee fired"));
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error("Error dismissing employee:", error);
        }
    };

    const handleChange = (date: Date, setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
        if (date) {
            setStartDate(date);
            const formattedDate = date.toISOString().slice(0, 10);
            setFieldValue("endOfCooperation", formattedDate);
        }
    };

    if (bookingsForEmployeeLoading || !branchId) return <Spinner />;

    return (
        <>
            <Modal
                size="lg"
                show={show}
                onHide={() => {
                    setStartDate(null);
                    onHide();
                }}
                centered
            >
                <Modal.Header className="border-0" closeButton>
                    <h5 className="m-0">{t("Dismissal of Employee")}</h5>
                </Modal.Header>
                <Formik
                    initialValues={{
                        employeeId: +selectedEmployee?.id,
                        endOfCooperation: selectedEmployee?.dismissalEmployee?.endDateCooperation || null,
                        reasonId: selectedEmployee?.dismissalEmployee?.employeeDismissalReason?.id || null,
                        comment: selectedEmployee?.dismissalEmployee?.comment || null,
                    }}
                    validationSchema={deleteEmployeeModalValidationSchema(t)}
                    onSubmit={async (values, actions) => {
                        try {
                            const { data } =
                                currentCompanyId &&
                                values.employeeId &&
                                (await getBookingsForEmployee({
                                    variables: {
                                        companyId: +currentCompanyId,
                                        employeeId: values.employeeId,
                                        removeCanceledBookings: false,
                                        filterByCompany: true,
                                        startDate: getNextDay(values.endOfCooperation),
                                        branchId: Number(branchId),
                                    },
                                }));
                            const isFutureBookingsEmployee =
                                data?.user?.company?.employees?.employees?.[0]?.bookings?.bookings?.length > 0;

                            if (isFutureBookingsEmployee) {
                                setShowBookingConflictModal(true);
                                setStartDate(null);
                                onHide();
                            } else {
                                setFormData(values);
                                toggleConfirmDeleting();
                                setStartDate(null);
                                onHide();
                            }
                        } catch (error) {
                            console.error("Error dismissing employee:", error);
                        } finally {
                            actions.setSubmitting(false);
                        }
                    }}
                >
                    {({ isSubmitting, setFieldValue, values, errors, touched }) => {
                        return (
                            <Form>
                                <Modal.Body style={{ padding: "16px 28px 16px 28px" }}>
                                    <Row className="py-2 py-sm-3">
                                        <Col xs={3} className="my-auto">
                                            <strong>
                                                <span className="fs-7">{t("Name")}</span>
                                            </strong>
                                        </Col>
                                        <Col xs={9}>
                                            <span className="fs-7">{selectedEmployee?.name}</span>
                                        </Col>
                                    </Row>

                                    <Row className="py-2 py-sm-3">
                                        <Col xs={3} className="my-auto">
                                            <strong>
                                                <span className="fs-7">{t("Last name")}</span>
                                            </strong>
                                        </Col>
                                        <Col xs={9}>
                                            <span className="fs-7">{selectedEmployee?.surname}</span>
                                        </Col>
                                    </Row>

                                    <Row className="py-2 py-sm-3">
                                        <Col xs={3} className="my-auto">
                                            <strong>
                                                <span className="fs-7">{t("Position")}</span>
                                            </strong>
                                        </Col>
                                        <Col xs={9}>
                                            <span className="fs-7">{selectedEmployee?.position.name}</span>
                                        </Col>
                                    </Row>

                                    <Row className="my-2 my-sm-3">
                                        <Col sm={3} className="pe-0 pt-2 mb-2 mb-sm-0">
                                            <label htmlFor="deleteEmployeeModal-endOfCooperation-field">
                                                <strong className="text-dark fs-7">{t("End Date of")}</strong>{" "}
                                                <strong className="fs-7 required-label">{t("Cooperation")}</strong>
                                            </label>
                                        </Col>
                                        <Col sm={9} className="text-dark">
                                            <div className="position-relative">
                                                <DatePicker
                                                    id="deleteEmployeeModal-endOfCooperation-field"
                                                    name="endOfCooperation"
                                                    className={`form-control fs-7 ${errors.endOfCooperation && touched.endOfCooperation ? "border-danger" : ""} `}
                                                    wrapperClassName="w-100"
                                                    selected={startDate ? startDate : selectedDate}
                                                    placeholderText={t("dd.mm.yyyy")}
                                                    onChange={(date) => handleChange(date, setFieldValue)}
                                                    dateFormat="dd.MM.yyyy"
                                                />
                                                {errors.endOfCooperation && touched.endOfCooperation && (
                                                    <i className="bi bi-exclamation-circle fs-20 position-absolute top-50 end-0 translate-middle-y text-danger p-1 me-7 fw-bold"></i>
                                                )}
                                                <label
                                                    htmlFor="deleteEmployeeModal-endOfCooperation-field"
                                                    className={
                                                        "position-absolute cursor-pointer" +
                                                        " top-50 end-0 translate-middle-y me-6 clean-line-height"
                                                    }
                                                >
                                                    <i className="bi bi-calendar4-week d-inline-block"></i>
                                                </label>
                                            </div>
                                            <ErrorMessage name="endOfCooperation">
                                                {(msg) => <div className="invalid-feedback d-block fs-7">{msg}</div>}
                                            </ErrorMessage>
                                        </Col>
                                    </Row>
                                    <Row className="mb-2 mb-sm-3">
                                        <Col sm={3} className="pe-0 pt-2 mb-2 mb-sm-0">
                                            <label htmlFor="Reason-for-Dismissal" className="required-label">
                                                <strong className="text-dark fs-7">{t("Reason for Dismissal")}</strong>
                                            </label>
                                        </Col>
                                        <Col sm={9} className="text-dark fs-7">
                                            <ReactSelect
                                                id="deleteEmployeeModal-reasonId-field"
                                                name="reasonId"
                                                placeholder={t("Reason for Dismissal")}
                                                options={dismissalReasonOptions}
                                                value={dismissalReasonOptions?.find(
                                                    (option: Option) => option.value === values?.reasonId
                                                )}
                                                onChange={(selectedOption: Option) => {
                                                    setFieldValue("reasonId", selectedOption.value);
                                                    selectedOption.value === 2 && setFieldValue("comment", null, false);
                                                }}
                                                isSearchable={true}
                                            />
                                            <ErrorMessage name="reasonId">
                                                {(msg) => <div className="invalid-feedback d-block fs-7">{msg}</div>}
                                            </ErrorMessage>
                                        </Col>
                                    </Row>

                                    <Row className="mb-2 mb-sm-3">
                                        <TextareaInputWithCounter
                                            key={`comment-${values.reasonId}`}
                                            name="comment"
                                            id="deleteEmployeeModal-comment-field"
                                            maxLength={200}
                                            labelCol={3}
                                            inputCol={9}
                                            placeholder={t("Explain the reason for dismissal")}
                                            label={t("Comment")}
                                            required={true}
                                            disabled={values.reasonId !== 4}
                                            disabledLabel={values.reasonId !== 4}
                                        />
                                    </Row>
                                </Modal.Body>
                                <Row className="justify-content-end p-6 gx-3">
                                    {selectedEmployee?.dismissalEmployee?.id && (
                                        <Col xs={4} lg={3} className="me-lg-auto">
                                            <Button
                                                variant="danger"
                                                className="text-truncate w-100"
                                                onClick={dismissDeleting}
                                            >
                                                {t("Cancel Dismissal")}
                                            </Button>
                                        </Col>
                                    )}
                                    <Col
                                        className={`${selectedEmployee?.dismissalEmployee?.id ? "col-4 col-lg-3" : "col-lg-3"}`}
                                    >
                                        <Button
                                            type="button"
                                            variant="outline-primary"
                                            className="h-100 fw-normal w-100"
                                            onClick={(e) => {
                                                setStartDate(null);
                                                onHide();
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            {t("Cancel")}
                                        </Button>
                                    </Col>
                                    <Col
                                        className={`${selectedEmployee?.dismissalEmployee?.id ? "col-4 col-lg-3" : "col-lg-3"}`}
                                    >
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="text-truncate w-100"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader variant="light" loadingText={t("Confirming...")} />
                                            ) : (
                                                t("Confirm")
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </Modal>

            <Modal
                show={showConfirmationModal}
                onHide={toggleConfirmDeleting}
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton className="border-0">
                    <Modal.Title>{t("Dismissal of Employee")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {t("Do you really want to dismiss")} {selectedEmployee?.name} {selectedEmployee?.surname}{" "}
                    {t("from the position of")} {selectedEmployee?.position?.name}?
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Col>
                        <Button
                            variant="outline-primary"
                            className="w-100 h-100 fw-normal"
                            onClick={toggleConfirmDeleting}
                        >
                            {t("Cancel")}
                        </Button>
                    </Col>
                    <Col>
                        <Button
                            variant="primary"
                            className="w-100 text-truncate"
                            onClick={confirmDeleting}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <SpinnerLoader variant="light" loadingText={t("Confirming...")} />
                            ) : (
                                t("Confirm")
                            )}
                        </Button>
                    </Col>
                </Modal.Footer>
            </Modal>
            <CustomModal
                show={showBookingConflictModal}
                handleClose={handleCloseBookingConflictModal}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("It is impossible to dismiss an employee.")}</h5>
                        </Row>
                    </Container>
                }
                body={
                    <Container>
                        <Row>
                            <Col>
                                <p className="mb-0">
                                    {t(
                                        "It is not possible to fire an employee because the" +
                                            " employee has a booking for this period. Go to the Logbook" +
                                            " to move or cancel them."
                                    )}
                                </p>
                            </Col>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("Go to Logbook"),
                        onClick: () => navigate(`/logbook/journal/${branchId}`),
                        variant: "primary",
                        className: "fw-normal",
                        positionButtons: "text-center",
                    },
                ]}
            />

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default DeleteEmployeeModal;